import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeSlash, CircleNotch } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Background image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/ac36dbfc-fc10-4323-8ec9-56eb581f7695/images/780a764c329024019954fd8c35c10a25cce083d556de42e08074ee77021a41f6.png')` 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-slate-900/90" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img 
            src="https://customer-assets.emergentagent.com/job_0246dde3-e1c1-4ddd-b1f0-cbdadc084ea8/artifacts/indb97if_WhatsApp%20Image%202026-02-08%20at%208.04.43%20PM%20%281%29.jpeg"
            alt="IQES Logo"
            className="w-32 h-32 object-contain mb-8 rounded-xl shadow-2xl"
          />
          <h1 className="text-4xl font-black mb-4 text-center tracking-tight">IQES LLC</h1>
          <p className="text-xl text-blue-200 mb-2">Low Voltage Solutions</p>
          <p className="text-slate-300 text-center max-w-md">
            Sistema de gestión de clientes, cotizaciones y pagos para soluciones de bajo voltaje.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_0246dde3-e1c1-4ddd-b1f0-cbdadc084ea8/artifacts/indb97if_WhatsApp%20Image%202026-02-08%20at%208.04.43%20PM%20%281%29.jpeg"
              alt="IQES Logo"
              className="w-20 h-20 object-contain mb-4 rounded-xl shadow-lg"
            />
            <h1 className="text-2xl font-black text-slate-900">IQES LLC</h1>
            <p className="text-slate-500">Low Voltage Solutions</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Bienvenido</h2>
              <p className="text-slate-500">Ingresa tus credenciales para acceder al CRM</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="form-label">
                  Correo electrónico
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="admin@iqeslowvoltage.com"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input pr-10"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar sesión'
                )}
              </button>
            </form>
          </div>

          <p className="text-center mt-6 text-sm text-slate-500">
            IQES LLC © 2026 - Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
}
